import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronDown, ShieldCheck, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Panel, Skeleton, EmptyState } from "../components/ui";
import TaxSettings from "../components/TaxSettings";
import InsurancePolicyDialog from "../components/InsurancePolicyDialog";
import Hint from "../components/Hint";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { useMoney } from "../context/MoneyContext";

function Stat({ label, value, sub, term }) {
  return (
    <div>
      {term ? <Hint term={term}><span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span></Hint>
        : <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>}
      <div className="mt-1 font-medium tabular text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function ScenarioCard({ title, scenario, meets12_62 }) {
  const { t } = useTranslation();
  if (!scenario) return null;
  return (
    <div className="rounded-xl border border-slate-100 p-3">
      <div className="text-xs font-semibold text-slate-700 mb-2">{title}</div>
      {meets12_62 !== undefined && (
        <div className={`text-[11px] mb-2 ${meets12_62 ? "text-emerald-600" : "text-amber-600"}`}>
          {meets12_62 ? t("insurance.meets1262") : t("insurance.notMeets1262")}
        </div>
      )}
      <div className="space-y-1 text-xs text-slate-500">
        <div className="flex justify-between"><span>{t("tax.grossGain")}</span><span className="tabular font-medium text-slate-700">{money(scenario.gross_gain ?? scenario.taxable_portion, "EUR")}</span></div>
        <div className="flex justify-between"><span>{t("tax.effectiveRate")}</span><span className="tabular">{scenario.effective_rate_pct}%</span></div>
        <div className="flex justify-between font-medium text-rose-600"><span>{t("tax.estimatedTax")}</span><span className="tabular">{money(scenario.tax_estimate ?? scenario.tax_estimate_per_payment, "EUR")}{scenario.tax_estimate_per_payment != null ? ` / ${t("insurance.perPayment")}` : ""}</span></div>
      </div>
    </div>
  );
}

function PolicyTaxScenarios({ policy }) {
  const { t } = useTranslation();
  const { marginalTaxRate, churchTaxPct } = useMoney();
  const [open, setOpen] = useState(false);
  const [ageNow, setAgeNow] = useState(40);
  const [monthlyAnnuity, setMonthlyAnnuity] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchScenarios = async () => {
    setLoading(true);
    try {
      const { data } = await api.insurancePolicyTaxScenarios(policy.id, {
        age_now: ageNow, marginal_rate_pct: marginalTaxRate, church_tax_pct: churchTaxPct,
        monthly_annuity_estimate: monthlyAnnuity || undefined,
      });
      setResult(data);
    } catch { toast.error(t("dialogs.failed")); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      <button data-testid={`policy-tax-toggle-${policy.id}`} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors">
        <TrendingUp size={15} /> {open ? t("insurance.hideTaxScenarios") : t("insurance.viewTaxScenarios")}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <span className="label block mb-1">{t("insurance.yourAgeNow")}</span>
              <input data-testid={`policy-age-input-${policy.id}`} type="number" className="input w-20 py-1.5 text-sm tabular" value={ageNow} onChange={(e) => setAgeNow(Number(e.target.value))} />
            </div>
            <div>
              <span className="label block mb-1">{t("insurance.monthlyAnnuityEstimate")}</span>
              <input type="number" className="input w-32 py-1.5 text-sm tabular" value={monthlyAnnuity} onChange={(e) => setMonthlyAnnuity(e.target.value)} placeholder={money(0, policy.currency)} />
            </div>
            <button data-testid={`policy-tax-fetch-${policy.id}`} onClick={fetchScenarios} disabled={loading} className="btn-outline py-1.5 text-sm">
              {t("insurance.viewTaxScenarios")}
            </button>
          </div>
          {result && (
            <div className="grid sm:grid-cols-3 gap-3">
              <ScenarioCard title={t("insurance.scenarioCashOutNow")} scenario={result.cash_out_now} meets12_62={result.cash_out_now?.meets_12_62_rule} />
              <ScenarioCard title={`${t("insurance.scenarioCashOutTerm")} (${t("insurance.ageAtTerm", { age: result.age_at_term_end })})`} scenario={result.cash_out_at_term} meets12_62={result.cash_out_at_term?.meets_12_62_rule} />
              {result.annuity_at_term && <ScenarioCard title={t("insurance.scenarioAnnuity")} scenario={result.annuity_at_term} />}
            </div>
          )}
          <div className="text-[11px] text-slate-400">{t("tax.disclaimer")}</div>
        </div>
      )}
    </div>
  );
}

function PolicyCard({ policy, onEdit, onDelete }) {
  const { t } = useTranslation();
  const p = policy.projection;
  const ccy = policy.currency;
  return (
    <Panel className="card p-6" testid={`policy-card-${policy.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
            <ShieldCheck size={20} className="text-brand-500" />
          </div>
          <div>
            <div className="font-heading font-semibold tracking-tight">{policy.provider}</div>
            <div className="text-sm text-slate-500">{policy.product_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button data-testid={`policy-edit-${policy.id}`} onClick={() => onEdit(policy)} className="text-slate-400 hover:text-brand-600 p-2 transition-colors"><Pencil size={16} /></button>
          <button data-testid={`policy-delete-${policy.id}`} onClick={() => onDelete(policy)} className="text-slate-400 hover:text-rose-500 p-2 transition-colors"><Trash2 size={16} /></button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label={t("insurance.totalContributed")} value={money(p.total_contributed_to_date, ccy)} />
        <Stat label={t("insurance.totalFees")} value={money(p.total_fees_to_date, ccy)} term="Zillmerung" />
        <Stat label={t("insurance.currentValue")} value={money(p.current_value, ccy)} sub={p.used_manual_override ? undefined : t("insurance.modelledValue")} />
        <Stat label={t("insurance.projectedAtTerm")} value={money(p.projected_value_at_term, ccy)} sub={t("insurance.termEndYear", { year: new Date(policy.start_date).getFullYear() + policy.term_years })} />
      </div>

      <div className="mt-4 flex items-center gap-2 text-sm">
        <span className="text-slate-500">{t("insurance.surrenderGainNow")}:</span>
        <span className={`font-medium tabular ${p.surrender_gain_now > 0 ? "text-emerald-600" : "text-slate-400"}`}>{money(p.surrender_gain_now, ccy)}</span>
      </div>

      <PolicyTaxScenarios policy={policy} />
    </Panel>
  );
}

export default function InsurancePolicies() {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.insurancePolicies().then((r) => setPolicies(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };
  const del = async (p) => {
    if (!window.confirm(t("insurance.deleteConfirm"))) return;
    try { await api.deleteInsurancePolicy(p.id); toast.success(t("insurance.delete")); load(); }
    catch { toast.error(t("dialogs.failed")); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label">{t("insurance.subtitle")}</span>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("insurance.title")}</h1>
        </div>
        <button data-testid="add-policy-btn" className="btn-primary" onClick={openAdd}><Plus size={18} /> {t("insurance.addPolicy")}</button>
      </div>

      <TaxSettings testid="insurance-tax-settings" />

      {loading ? (
        <div className="space-y-4">{[0, 1].map((i) => <Skeleton key={i} className="h-52" />)}</div>
      ) : policies.length === 0 ? (
        <EmptyState testid="insurance-empty" title={t("insurance.emptyTitle")} description={t("insurance.emptyDesc")}
          action={<button className="btn-primary" onClick={openAdd}><Plus size={18} /> {t("insurance.addPolicy")}</button>} />
      ) : (
        <div className="space-y-5">
          {policies.map((p) => <PolicyCard key={p.id} policy={p} onEdit={openEdit} onDelete={del} />)}
        </div>
      )}

      <InsurancePolicyDialog open={dialogOpen} policy={editing} onClose={() => setDialogOpen(false)} onDone={load} />
    </div>
  );
}
